import { Button } from '@/components/ui/button';
import { ArrowLeft, User, Settings, MapPin, Grid3x3, History, ChefHat, Monitor, Keyboard } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/authStore';
import { usePOSShopSettings } from '@/features/pos/hooks/useShopSettings';
import { useBusinessType } from '@/features/pos/hooks/useBusinessType';
import { useEffect, useState } from 'react';

interface POSHeaderProps {
  selectedArea?: string;
  selectedTable?: string;
  onSelectAreaTable?: () => void;
  onShowShortcuts?: () => void;
}

export const POSHeader = ({ selectedArea, selectedTable, onSelectAreaTable, onShowShortcuts }: POSHeaderProps) => {
  const navigate = useNavigate();
  const { company } = useAuthStore();
  const { data: settings } = usePOSShopSettings();
  const { features } = useBusinessType();
  const [currentTime, setCurrentTime] = useState('');
  const [currentDate, setCurrentDate] = useState('');

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setCurrentDate(now.toLocaleDateString('fr-DZ', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }));
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Use shop name from settings, fallback to company name
  const shopName = settings?.shop_name || company?.name || 'POS';

  return (
    <div className="bg-card border-b border-border shadow-sm">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left: Back Button & Shop Info */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/dashboard' })}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            لوحة التحكم / Tableau de bord
          </Button>
          <div className="h-8 w-px bg-gray-300" />
          <div>
            <p className="text-lg font-bold text-gray-900">{shopName}</p>
            {settings?.shop_type && (
              <p className="text-xs text-gray-500 capitalize">
                {settings.shop_type === 'restaurant' && '🍽️ Restaurant'}
                {settings.shop_type === 'cafe' && '☕ Café'}
                {settings.shop_type === 'bakery' && '🥖 Boulangerie'}
                {settings.shop_type === 'retail' && '🏪 Commerce'}
              </p>
            )}
          </div>
        </div>

        {/* Center: Area/Table Selector */}
        {settings?.enable_table_service && onSelectAreaTable && (
          <div className="flex-1 flex justify-center">
            <Button
              variant="outline"
              onClick={onSelectAreaTable}
              className="gap-2 border-2 hover:border-emerald-500 hover:bg-emerald-50"
            >
              {selectedArea ? (
                <>
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">{selectedArea}</span>
                  {selectedTable && (
                    <>
                      <span className="text-gray-400">/</span>
                      <Grid3x3 className="w-4 h-4 text-emerald-600" />
                      <span className="font-medium">{selectedTable}</span>
                    </>
                  )}
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4" />
                  <span>Sélectionner Table / اختر الطاولة</span>
                </>
              )}
            </Button>
          </div>
        )}

        {/* Right: Date, Time & Actions */}
        <div className="flex items-center gap-6">
          {/* Date & Time */}
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{currentTime}</p>
            <p className="text-xs text-gray-500">{currentDate}</p>
          </div>

          <div className="h-8 w-px bg-gray-300" />

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Customer Display - Restaurant & Cafe only */}
            {features.hasCustomerDisplay && (
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                onClick={() => navigate({ to: '/apps/pos/display' })}
              >
                <Monitor className="w-4 h-4" />
                Affichage
              </Button>
            )}
            
            {/* Kitchen Display - Restaurant, Cafe, Bakery */}
            {features.hasKitchenDisplay && (
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                onClick={() => navigate({ to: '/apps/pos/kitchen' })}
              >
                <ChefHat className="w-4 h-4" />
                Cuisine
              </Button>
            )}
            
            {/* History - Always visible */}
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2"
              onClick={() => navigate({ to: '/apps/pos/history' })}
            >
              <History className="w-4 h-4" />
              Historique
            </Button>
            
            <Button variant="ghost" size="sm" className="gap-2">
              <User className="w-4 h-4" />
              Caissier
            </Button>
            {onShowShortcuts && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowShortcuts}
                className="gap-1.5"
                title="Raccourcis clavier (Shift+?)"
              >
                <Keyboard className="w-4 h-4" />
                <kbd className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded">?</kbd>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/apps/pos/admin/settings' })}
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
